import { useMemo } from 'react';
import { useAppSelector } from '../../../redux/helper';
import type { BackofficeProduct } from '../../../redux/backoffice/productsSlice';
import {
  formatMoney,
  formatNumberDecimal,
  getCurrencyDisplayFormat,
  roundMoney,
} from '../../../utils/libs';
import { toFiniteNumber } from '../../../utils/backofficeProductView';
import { SIZE_OPTIONS } from '../../products/newproducts/productFormTypes';

/** Currency label for the current location ("Ksh"), as the Inventory list shows it. */
export const useCurrencyText = (): string => {
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  return useMemo(() => getCurrencyDisplayFormat(currency), [currency]);
};

/** "Ksh 1,250"; "—" when unknown. */
export const formatMoneyText = (
  currencyText: string,
  value: number | null | undefined,
): string => {
  if (value == null || !Number.isFinite(value)) return '—';
  const sign = value < 0 ? '-' : '';
  return `${sign}${currencyText} ${formatMoney(Math.abs(value))}`;
};

/**
 * The unit a product is sold per when it is priced per unit ("Kg"); null for
 * products sold per item.
 */
export const getSellUnit = (product: BackofficeProduct): string | null =>
  product.bp_product_price_type === 'unit'
    ? String(product.bp_product_unit_of_mass || '').trim() || 'Unit'
    : null;

/** "Ksh 180", or "Ksh 180 per Kg" for products sold per unit. */
export const formatPriceText = (
  currencyText: string,
  price: unknown,
  sellUnit: string | null,
): string => {
  const value = toFiniteNumber(price);
  const money = formatMoneyText(currencyText, value);
  return value != null && sellUnit ? `${money} per ${sellUnit}` : money;
};

/** Selling minus buying price; null when either is missing. */
export const getProfitPerUnit = (
  sellingPrice: unknown,
  buyingPrice: unknown,
): number | null => {
  const selling = toFiniteNumber(sellingPrice);
  const buying = toFiniteNumber(buyingPrice);
  return selling == null || buying == null
    ? null
    : roundMoney(selling - buying);
};

/** How a stock value was worked out: "3 × Ksh 250", "42.5 Kg × Ksh 180". */
export const formatStockValueCaption = (
  currencyText: string,
  quantity: unknown,
  price: unknown,
  sellUnit: string | null,
): string | undefined => {
  const unitPrice = toFiniteNumber(price);
  if (unitPrice == null) return undefined;
  const qty = toFiniteNumber(quantity) ?? 0;
  if (qty <= 0) return 'Nothing in stock';
  const qtyText = `${formatNumberDecimal(qty)}${sellUnit ? ` ${sellUnit}` : ''}`;
  return `${qtyText} × ${formatMoneyText(currencyText, unitPrice)}`;
};

export const getSizeLabel = (size: unknown): string => {
  const value = String(size ?? '').trim();
  if (!value) return '';
  return SIZE_OPTIONS.find(option => option.value === value)?.label || value;
};
